import type { Resolution, ResourceType } from '@carboncopyco/types';
import { Debug, removeUndefinedValues } from '@carboncopyco/utils';
import { createBaseApi } from '../utils';

Debug.extend('demostack-data:api:analyticsServiceApi');

export interface AnalyticsActivityBaseArgs {
  organizationId: string;
  start: string;
  end: string;
  timezone: string;
  resolution: Resolution;
}

export interface GetAnalyticsActivityArgs extends AnalyticsActivityBaseArgs {
  resourceTypes: ResourceType[];
  demoId?: string;
  movingAverage?: string;
}

export interface GetAnalyticsActivityByResourceIdArgs
  extends AnalyticsActivityBaseArgs {
  resourceType?: ResourceType;
  resourceId: string;
  workspaceIds?: number[];
}

export interface AnalyticsActivity {
  timestamp: string;
  values: AnalyticsActivityValues;
}

export interface AnalyticsActivityValues {
  avgDuration: number;
  occurrences: number;
}

export interface AnalyticsActivityResponse {
  metadata: {
    start: string;
    end: string;
    resolution: Resolution;
  };
  results: AnalyticsActivity[];
}

export const [analyticsServiceApi, initAnalyticsServiceApi] = createBaseApi({
  name: 'analyticsApi',
  tagTypes: ['AnalyticsIndexPage'],
  endpoints: (builder) => ({
    getAnalyticsActivity: builder.query<
      AnalyticsActivityResponse,
      GetAnalyticsActivityArgs
    >({
      query: ({
        organizationId,
        demoId,
        start,
        end,
        timezone,
        resolution,
        resourceTypes,
        movingAverage,
      }) => {
        return {
          url: '/activity',
          method: 'GET',
          params: {
            resolution,
            organizationId,
            resourceTypes: resourceTypes?.join('&resource_types='),
            ...removeUndefinedValues({
              demoId,
              start,
              end,
              timezone,
              movingAverage,
            }),
          },
        };
      },
      providesTags: ['AnalyticsIndexPage'],
    }),

    getAnalyticsActivityByResourceId: builder.query<
      AnalyticsActivityResponse,
      GetAnalyticsActivityByResourceIdArgs
    >({
      query: ({
        organizationId,
        start,
        end,
        timezone,
        resolution,
        resourceType,
        resourceId,
      }) => {
        return {
          url: '/activity/resource',
          method: 'GET',
          params: {
            resourceId,
            resolution,
            organizationId,
            ...removeUndefinedValues({
              start,
              end,
              timezone,
              resourceType,
            }),
          },
        };
      },
      providesTags: ['AnalyticsIndexPage'],
    }),
  }),
});

export const {
  useLazyGetAnalyticsActivityByResourceIdQuery,
  useLazyGetAnalyticsActivityQuery,
} = analyticsServiceApi;
